import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import type { GalleryPagination as PagyShape } from "@/types/content-gallery"

/**
 * OKS-39 — Gallery pagination footer.
 *
 * Hybrid (UX Spec §3.4 / §7):
 *   - Desktop ≥768 px: numbered pager via shadcn `Pagination` primitive,
 *     plus prev/next caret buttons. Compact ellipsis when total pages
 *     overflow a 7-slot window.
 *   - Mobile <768 px: single "Load more" button (or "Previous" /
 *     "Next" pair if currently mid-paged).
 *
 * Pagy's `:empty_page` extra (OKS-110) handles out-of-range page
 * numbers via redirect — the FE never has to defend against `page > pages`.
 */
interface GalleryPaginationProps {
  pagination: PagyShape
  perPage: number
  onPageChange: (page: number) => void
  className?: string
}

export function GalleryPagination({
  pagination,
  perPage,
  onPageChange,
  className,
}: GalleryPaginationProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { page, pages, count, prev, next } = pagination

  if (count === 0) return null

  const fromIdx = (page - 1) * perPage + 1
  const toIdx = Math.min(page * perPage, count)
  const summary = t("content_gallery.pagination.summary", {
    from: fromIdx,
    to: toIdx,
    count,
  })

  if (isMobile) {
    return (
      <nav
        aria-label={t("content_gallery.pagination.aria")}
        className={cn(
          "flex flex-col items-center gap-3 py-4 text-sm",
          className,
        )}
        data-testid="gallery-pagination"
      >
        <span className="text-muted-foreground">{summary}</span>
        <div className="flex w-full items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={prev == null}
            onClick={() => prev != null && onPageChange(prev)}
            data-testid="gallery-pagination-prev"
          >
            <ChevronLeft aria-hidden className="size-4" />
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={next == null}
            onClick={() => next != null && onPageChange(next)}
            className="flex-1"
            data-testid="gallery-pagination-next"
          >
            {next != null
              ? t("content_gallery.pagination.load_more")
              : t("content_gallery.pagination.load_more")}
          </Button>
        </div>
      </nav>
    )
  }

  const slots = computePageSlots(page, pages)

  return (
    <Pagination
      aria-label={t("content_gallery.pagination.aria")}
      className={cn("py-4", className)}
      data-testid="gallery-pagination"
    >
      <PaginationContent>
        <PaginationItem>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={prev == null}
            onClick={() => prev != null && onPageChange(prev)}
            aria-label="Previous page"
            data-testid="gallery-pagination-prev"
          >
            <ChevronLeft aria-hidden className="size-4" />
          </Button>
        </PaginationItem>

        {slots.map((slot, idx) =>
          slot === "ellipsis" ? (
            <PaginationItem key={`ell-${idx}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={slot}>
              <PaginationLink
                href="#"
                isActive={slot === page}
                onClick={(e) => {
                  e.preventDefault()
                  if (slot !== page) onPageChange(slot)
                }}
                data-testid={`gallery-pagination-page-${slot}`}
              >
                {slot}
              </PaginationLink>
            </PaginationItem>
          ),
        )}

        <PaginationItem>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={next == null}
            onClick={() => next != null && onPageChange(next)}
            aria-label="Next page"
            data-testid="gallery-pagination-next"
          >
            <ChevronRight aria-hidden className="size-4" />
          </Button>
        </PaginationItem>
      </PaginationContent>

      <span
        className="text-muted-foreground ml-4 self-center text-xs"
        data-testid="gallery-pagination-summary"
      >
        {summary}
      </span>
    </Pagination>
  )
}

/**
 * 7-slot windowed page list with ellipsis on either side.
 *   pages ≤ 7      → [1..pages]
 *   active near 1  → [1, 2, 3, 4, 5, "...", pages]
 *   active near N  → [1, "...", pages-4..pages]
 *   middle         → [1, "...", page-1, page, page+1, "...", pages]
 */
type Slot = number | "ellipsis"
function computePageSlots(active: number, total: number): Slot[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  if (active <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", total]
  }
  if (active >= total - 3) {
    return [1, "ellipsis", total - 4, total - 3, total - 2, total - 1, total]
  }
  return [1, "ellipsis", active - 1, active, active + 1, "ellipsis", total]
}
